import UserAPI, { User } from './api'
import {
  FeaturedProductUpdate,
  Author,
  FeaturedTopicFromUnified
} from '../widget-featured-topics/index.sync'
import { Context } from '../../utils/globals'

const TYPE_PRODUCT_UPDATE = 'productUpdate'

interface UnifiedTopic {
  id: number
  publicId: number
  title: string
  content: string
  contentType: 'conversation' | 'question' | 'article' | 'idea' | 'productUpdate'
  featuredImage: string
  lastActivityAt: string
  publishedAt: string
  createdAt: string
  productAreas: string[]
  replyCount: number
  likes: number
  likeSet: number[]
  votes: number
  voteSet: string[]
  views: number
  label: string
  publicLabel: string
  voteCount: number
  trashed: boolean
  status: string
  ideaStatus: {
    name: string
    color: string
    bgColor: string
  }
  author: {
    id: number
  }
}

interface UnifiedResponse {
  data: {
    result: UnifiedTopic[]
  }
}

interface UnifiedInternalResponse {
  data: {
    result: {
      topics: UnifiedTopic[]
    }
  }
}

interface ProductUpdateIdMap {
  [id: string]: FeaturedProductUpdate | undefined
}

interface TopicIdMap {
  [id: string]: FeaturedTopicFromUnified | undefined
}

class Unified {
  context: Context
  url: string
  api: UserAPI
  UNIFIED_GET_INTERNAL_TOPICS = '/v2/internal/topics'
  UNIFIED_GET_TOPICS = '/v2/topics'

  constructor (context: Context) {
    this.context = context
    const host = process.env.app_config_unified_api_url
    this.url = host ? `https://${host}` : 'http://unified.dev.insided.com'
    this.api = new UserAPI(context)
  }

  private userToAuthor (u: User): Author {
    return {
      avatar: u.avatar,
      id: u.userid,
      name: u.username,
      url: `/members/${u.username.toLowerCase()}-${u.userid}`,
      userTitle: u.usertitle
    }
  }

  public async getProductUpdatesIndexedById (topicIds: number[]) {
    if (!topicIds.length) {
      return {}
    }

    this.context.logger(`PU Topics: ${JSON.stringify(topicIds)}`, 'warning')

    const topicInfoResult: UnifiedResponse = await this.context.axios.get(`${this.url}${this.UNIFIED_GET_TOPICS}`, {
      params: {
        contentType: TYPE_PRODUCT_UPDATE,
        topicIds: topicIds
      }
    })

    this.context.logger(`PU Topics: ${JSON.stringify(topicInfoResult.data)}`, 'warning')

    const topics: ProductUpdateIdMap = {}
    for (const topic of topicInfoResult.data.result) {
      if (topic.status !== 'published') continue
      if (topic.trashed) continue
      if (topic.contentType !== 'productUpdate') continue

      topics[topic.id] = {
        id: topic.id,
        publicId: topic.publicId,
        votes: topic.voteSet,
        likes: topic.likes,
        likeSet: topic.likeSet,
        createdAt: topic.createdAt,
        title: topic.title,
        content: topic.content,
        contentType: topic.contentType,
        featuredImage: topic.featuredImage,
        lastActivityAt: topic.lastActivityAt,
        publishedAt: topic.publishedAt,
        productAreas: topic.productAreas,
        replyCount: topic.replyCount,
        views: topic.views,
        label: topic.label,
        voteCount: topic.voteCount,
        publicLabel: topic.publicLabel,
        url: `product-updates/${topic.id}`,
        author: this.userToAuthor(await this.api.getUser(topic.author.id))
      }
    }

    return topics
  }

  public async getTopicsIndexedByPublicId (topicIds: number[]): Promise<TopicIdMap> {
    if (!topicIds.length) {
      return {}
    }

    this.context.logger(`Topics: ${JSON.stringify(topicIds)}`, 'warning')

    const topicInfoResult: UnifiedInternalResponse = await this.context.axios.post(`${this.url}${this.UNIFIED_GET_INTERNAL_TOPICS}`, {
      filters: {
        trashed: false,
        status: ['published'],
        publicId: topicIds
      }
    })

    this.context.logger(`Topics: ${JSON.stringify(topicInfoResult.data)}`, 'warning')

    const topics: TopicIdMap = {}
    for (const topic of topicInfoResult.data.result.topics) {
      topics[topic.publicId] = {
        id: topic.id,
        publicId: topic.publicId,
        title: topic.title,
        content: topic.content,
        contentType: topic.contentType,
        likeSet: topic.likeSet,
        featuredImage: topic.featuredImage ?? null,
        lastActivityAt: topic.lastActivityAt,
        createdAt: topic.createdAt,
        productAreas: topic.productAreas ?? [],
        replyCount: topic.replyCount,
        likes: topic.likes,
        votes: topic.voteSet,
        voteCount: topic.voteCount,
        views: topic.views,
        label: topic.label ?? '',
        publicLabel: topic.publicLabel ?? '',
        publishedAt: topic.publishedAt,
        url: topic.contentType === 'idea' ? `ideas/${topic.publicId}` : `/fid-1/tid-${topic.publicId}`,
        ideationStatus: {
          name: topic.ideaStatus.name,
          bgColor: `#${topic.ideaStatus.bgColor}`,
          color: `#${topic.ideaStatus.color}`
        },
        author: this.userToAuthor(await this.api.getUser(topic.author.id)),
        hasCurrentUserVoted: topic.voteSet.some(v => parseInt(v) === this.context.userId)
      }
    }

    return topics
  }
}

export default Unified
